import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";

import { imageSource, Strings } from "@app/constants";
import { FormProvider, VehicleCard } from "@app/components";
import { Spacing } from "@app/components/spacing";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { useRequestState } from "@app/hooks";
import { useScroll } from "@app/components/use-scroll/use-scroll";
import { ScrollTopButton } from "@app/components/use-scroll/scroll-to-top-button";
import { Footer } from "@app/modules/footer";

import { selectBrandState, selectVehicleTypeState } from "../selector";
import { brand, findProductIdBy, findWheelers } from "../slice";

const CategoryPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const methods = useForm();

  const [searchText, setSearchText] = useState("");
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<"two" | "four">("two");

  const { data: brandData } = useRequestState({
    stateSelector: selectBrandState,
  });
  const { wheelers, loading, error } = useSelector(selectVehicleTypeState);

  const vehicleTypeMap = {
    two: "Two Wheeler",
    four: "Four Wheeler",
  };

  // useEffect(() => {
  //   dispatch(brand());
  // }, [dispatch]);

  // useEffect(() => {
  //   dispatch(
  //     findWheelers({
  //       limit: 1000,
  //       page: 1,
  //       vehicleType: vehicleTypeMap[selectedVehicle],
  //     })
  //   );
  // }, [dispatch, selectedVehicle]);

  const filteredBrands = (brandData || []).filter((brand: any) =>
    brand?.name?.toLowerCase()?.includes(searchText?.toLowerCase())
  );

  const handleCardSelect = (vehicleId: number) => {
    setSelectedCardId(vehicleId);
  };

  const handleViewDetails = (vehicleId: number) => {
    navigate(`${PATH_DASHBOARD.productDetail}/${vehicleId}`);
  };
  const { scrollRef, contactRef, scrollToTop, scrollToContact, showScrollTop } =
    useScroll();

  return (
    <FormProvider methods={methods}>
      <Box ref={scrollRef}>
        <Stack
          sx={{
            width: "100%",
            height: { xs: 350, sm: 450, md: 550 },
            position: "relative",
          }}
        >
          <Box
            component="img"
            src={imageSource.car}
            alt="car background"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <Stack
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              color: theme.palette.common.white,
              textAlign: "center",
              px: 2,
            }}
          >
            <Typography
              fontFamily={"Mulish"}
              sx={{ fontSize: 45, fontWeight: 800, mb: 1 }}
            >
              {Strings.categories.categories}
            </Typography>
            <Typography
              fontWeight={"500"}
              fontFamily={"Inter"}
              sx={{
                maxWidth: { xs: "90%", sm: "80%", md: "60%" },
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                color: theme.palette.common.white,
              }}
            >
              {Strings.categrey.exploreAWide}
            </Typography>
          </Stack>
        </Stack>
        <Stack sx={{ padding: 6 }}>
          <Stack sx={{ width: "100%" }}>
            <Typography
              fontFamily={"Work Sans"}
              fontWeight={"700"}
              sx={{ fontSize: 16, color: theme.palette.common.black }}
            >
              {Strings.categories.exploreByBrands}
            </Typography>
          </Stack>

          <Stack alignItems="center" mt={4} sx={{ px: 13 }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                overflowX: "auto",
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  maxWidth: 5 * 140,
                  margin: "0 auto",
                  px: 2,
                }}
              >
                {filteredBrands?.map((item: any, index: number) => {
                  const isValidImage =
                    item.image &&
                    typeof item.image === "string" &&
                    item.image.trim() !== "{}" &&
                    item.image.trim() !== "";

                  return (
                    <Stack
                      key={index}
                      sx={{
                        minWidth: 128,
                        height: 128,
                        border: 1,
                        borderRadius: 0.5,
                        cursor: "pointer",
                        borderColor: theme.palette.primary.light,
                        background: `linear-gradient(180deg, ${theme.palette.grey[50]} 0%, ${theme.palette.primary.lighter} 100%)`,
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        textAlign: "center",
                        flexShrink: 0,
                      }}
                    >
                      {isValidImage ? (
                        <img
                          src={item.image}
                          loading="lazy"
                          alt={item.title || "Brand Logo"}
                          style={{
                            width: 62,
                            height: 62,
                            objectFit: "contain",
                          }}
                        />
                      ) : item.name ? (
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.grey[900],
                          }}
                        >
                          {item.name}
                        </Typography>
                      ) : null}
                    </Stack>
                  );
                })}
              </Box>
            </Box>
          </Stack>

          <Stack sx={{ width: "100%" }}>
            <Typography
              fontFamily={"Work Sans"}
              fontWeight={"700"}
              sx={{ fontSize: 16, color: theme.palette.grey[500], mt: 10 }}
            >
              {Strings.categories.browseByWheeler}
            </Typography>
            <Spacing top={6} />
            <Stack direction={"row"} gap={4} sx={{ cursor: "pointer" }}>
              <Typography
                fontFamily={"Work Sans"}
                fontWeight={"600"}
                sx={{
                  fontSize: 14,
                  color: theme.palette.grey[600],
                  borderBottom:
                    selectedVehicle === "two"
                      ? `2px solid ${theme.palette.grey[600]}`
                      : "none",
                }}
                onClick={() => setSelectedVehicle("two")}
              >
                {Strings.categories.twoWheeler}
              </Typography>
              <Typography
                fontFamily={"Work Sans"}
                fontWeight={"600"}
                sx={{
                  fontSize: 14,
                  color: theme.palette.grey[600],
                  borderBottom:
                    selectedVehicle === "four"
                      ? `2px solid ${theme.palette.grey[600]}`
                      : "none",
                }}
                onClick={() => setSelectedVehicle("four")}
              >
                {Strings.categories.fourWheeler}
              </Typography>
            </Stack>
            <Spacing top={5} />

            {loading ? (
              <Typography>{Strings.categories.loadingVehicles}</Typography>
            ) : error ? (
              <Typography color="error">
                {Strings.categories.errorLoadingVehicles}: {error?.toString()}
              </Typography>
            ) : wheelers?.length > 0 ? (
              <Grid container spacing={2}>
                {wheelers.map((vehicle: any) => (
                  <Box
                    key={vehicle.id}
                    sx={{
                      width: { xs: "100%", sm: "50%", md: "33.33%" },
                      p: 1,
                    }}
                  >
                    <VehicleCard
                      vehicle={{
                        id: vehicle.id,
                        name: vehicle.name,
                        image:
                          vehicle.brand?.image &&
                          vehicle.brand.image !== "null" &&
                          vehicle.brand.image !== "{}"
                            ? vehicle.brand.image
                            : "https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg?cs=srgb&dl=pexels-mikebirdy-170811.jpg&fm=jpg",
                        price: `$${vehicle.price.toLocaleString()}`,
                        categories: "hello",
                        engineType: vehicle.type || "Not Available",
                        fuelType: vehicle.fuelType || "Not Available",
                        driven: vehicle.engine?.toString() || "Not Available",
                      }}
                      selectedCardId={selectedCardId}
                      onSelect={() => handleCardSelect(vehicle.id)}
                      onViewDetails={handleViewDetails}
                    />
                  </Box>
                ))}
              </Grid>
            ) : (
              <Typography>
                {Strings.categrey.noWheelersFound(selectedVehicle)}
              </Typography>
            )}

            <Spacing top={5} />
            {selectedCardId && wheelers?.length > 0 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "700",
                    fontSize: 14,
                    color: theme.palette.grey[600],
                    mb: 2,
                  }}
                >
                  {Strings.categories.matchingWheelers}
                </Typography>
                <Grid container spacing={3}>
                  {wheelers
                    .filter((item: any) => item.id === selectedCardId)
                    .map((item: any) => (
                      <Box
                        key={item.id}
                        sx={{
                          width: { xs: "100%", sm: "50%", md: "33.33%" },
                          p: 1,
                        }}
                      >
                        <VehicleCard
                          vehicle={{
                            id: item.id,
                            name: item.name,
                            image:
                              item.images?.[0]?.url ||
                              "https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg?cs=srgb&dl=pexels-mikebirdy-170811.jpg&fm=jpg",
                            price: `₹${item.price.toLocaleString()}`,
                            categories: item.category?.name,
                            engineType: item.type || "Not Available",
                            fuelType: item.fuelType || "Not Available",
                            driven: item.engine
                              ? String(item.engine)
                              : "Not Available",
                          }}
                          selectedCardId={selectedCardId}
                          onSelect={() => handleCardSelect(item.id)}
                          onViewDetails={handleViewDetails}
                        />
                      </Box>
                    ))}
                </Grid>
              </>
            )}
          </Stack>
        </Stack>
        <div ref={contactRef} />
        {/* <Footer /> */}
        {showScrollTop && <ScrollTopButton onClick={scrollToTop} />}
      </Box>
    </FormProvider>
  );
};

export { CategoryPage };
